// Subscription tier types
export type SubscriptionTier = 'free' | 'personal' | 'professional';

export interface SubscriptionInfo {
  tier: SubscriptionTier;
  displayName: string;
  description: string;
  features: string[];
}

// Subscription tier definitions
export const SUBSCRIPTION_TIERS: Record<SubscriptionTier, SubscriptionInfo> = {
  free: {
    tier: 'free',
    displayName: 'Free',
    description: 'Basic access to image metadata extraction',
    features: [
      'Basic metadata extraction',
      'Limited file uploads'
    ]
  },
  personal: {
    tier: 'personal',
    displayName: 'Personal',
    description: 'Enhanced features for personal use',
    features: [
      'All Free features',
      'Advanced metadata extraction'
    ]
  },
  professional: {
    tier: 'professional',
    displayName: 'Professional',
    description: 'Full access with professional features',
    features: [
      'All Personal features',
      'Unlimited file uploads',
      'API access'
    ]
  }
};

// Subscription tier hierarchy (for access checking)
export const TIER_HIERARCHY: Record<SubscriptionTier, number> = {
  free: 0,
  personal: 1,
  professional: 2
};

// Helper function to check if a tier has access to a feature
export function hasTierAccess(userTier: SubscriptionTier, requiredTier: SubscriptionTier): boolean {
  return TIER_HIERARCHY[userTier] >= TIER_HIERARCHY[requiredTier];
}

// Helper function to get subscription info
export function getSubscriptionInfo(tier: SubscriptionTier | null | undefined): SubscriptionInfo {
  if (!tier || !(tier in SUBSCRIPTION_TIERS)) {
    return SUBSCRIPTION_TIERS.free;
  }
  return SUBSCRIPTION_TIERS[tier];
}

// Helper function to normalize tier name (case-insensitive, accepts slugs)
export function normalizeTier(tier: string | null | undefined): SubscriptionTier {
  if (!tier) return 'free';
  const normalized = tier.toLowerCase().trim();
  if (normalized in SUBSCRIPTION_TIERS) {
    return normalized as SubscriptionTier;
  }
  // Map common slugs/aliases (e.g. "personal_plan", "personal-plan", "pro")
  if (normalized.includes('professional') || normalized === 'pro') return 'professional';
  if (normalized.includes('personal') || normalized === 'personal') return 'personal';
  if (normalized.includes('free')) return 'free';
  return 'free';
}
